import { createContext, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldOff, Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MODULE_LABEL, requiredModules, bannerModule, grantedOf, restrictionsActive } from "@/lib/moduleGate";

/* Shown for direct URLs to a module the branch is not granted (enforcement ON only). Backend still authorizes every call. */
export function ModuleUnavailable({ modules }) {
  const auth = useAuth();
  const { user } = auth;
  const labels = (modules || []).map((m) => MODULE_LABEL[m] || m).join(" or ");
  const home = user?.role === "cafe_owner" ? "/owner/start" : user?.role === "captain" ? "/captain/dashboard" : "/manager/dashboard";
  const states = user?.branch_id ? auth.effectiveModules?.[user.branch_id]?.module_state || {} : {};
  const revoked = (modules || []).filter((m) => ["revoked", "winding_down"].includes(states[m]?.status));
  return (
    <div className="max-w-xl mx-auto mt-16 rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="module-unavailable">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-50 text-amber-700 grid place-items-center mb-4"><ShieldOff className="w-6 h-6" /></div>
      <h1 className="font-display text-2xl font-bold text-slate-900">{labels} is not enabled for this branch</h1>
      <p className="text-sm text-slate-500 mt-2">This module is not part of your branch's current subscription. Existing records stay available for reading. Contact your Cafe Owner or Super Admin to enable it.</p>
      {revoked.length > 0 && (
        <p className="text-sm text-orange-900 bg-orange-50 border border-orange-200 rounded-md px-3 py-2 mt-4" data-testid="module-unavailable-completion">
          Access to {revoked.map((m) => MODULE_LABEL[m]).join(", ")} was removed while work was open. Orders accepted before the cutoff can still be finished in the{" "}
          <Link to="/completion-only" className="font-semibold underline" data-testid="module-unavailable-completion-link">Completion-only view</Link>.
        </p>
      )}
      <Link to={home} className="inline-block mt-5 text-sm font-semibold text-brand-900 underline" data-testid="module-unavailable-home">Back to dashboard</Link>
    </div>
  );
}

/* Wraps a routed page: blocks when none of the required modules is granted; shows an explanatory banner for partial access
   (e.g. QR-only branch inside POS Billing, where the QR inbox lives). */
const GateCtx = createContext(0); // depth: 0 = Layout-route gate (pass-through), 1 = page gate inside the Layout content column

export default function ModuleGate({ children }) {
  const auth = useAuth();
  const { pathname } = useLocation();
  const depth = useContext(GateCtx);
  if (depth === 0) return <GateCtx.Provider value={1}>{children}</GateCtx.Provider>; // every gated page is inside Layout → decide at the page gate
  if (depth >= 2) return children;
  const required = requiredModules(pathname);
  if (!required || !restrictionsActive(auth)) return children;
  const granted = grantedOf(auth, required);
  if (!granted.length) return <ModuleUnavailable modules={required} />;
  const primary = bannerModule(pathname);
  const missing = primary && !granted.includes(primary) ? [primary] : [];
  const bid = auth.user?.branch_id;
  const states = bid ? auth.effectiveModules?.[bid]?.module_state || {} : {};
  const winding = granted.filter((m) => states[m]?.status === "winding_down");
  return (
    <GateCtx.Provider value={2}>
      {missing.length > 0 && (
        <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2 flex items-start gap-2" data-testid="module-partial-banner">
          <Info size={14} className="mt-0.5 shrink-0" /><span>{missing.map((m) => MODULE_LABEL[m]).join(", ")} is not enabled for this branch — only {granted.map((m) => MODULE_LABEL[m]).join(", ")} actions are available here. Other actions will be refused by the server.</span>
        </div>
      )}
      {winding.length > 0 && (
        <div className="mb-4 rounded-md bg-orange-50 border border-orange-200 text-orange-900 text-xs px-3 py-2 flex items-start gap-2" data-testid="module-lifecycle-banner">
          <Info size={14} className="mt-0.5 shrink-0" /><span>{winding.map((m) => MODULE_LABEL[m]).join(", ")} is <b>winding down</b> for this branch: new orders are closed. Orders that were open at the cutoff can still be completed (first KOT, first bill, payments, reprints); items cannot be added. Blocked actions are refused by the server with an explanation. <Link to="/completion-only" className="font-semibold underline" data-testid="module-lifecycle-completion-link">Open Completion-only view</Link></span>
        </div>
      )}
      {children}
    </GateCtx.Provider>
  );
}
